import * as d3 from 'd3';
import cloud from 'd3-cloud';

/**
 * Professional Word Cloud Renderer using d3-cloud
 * Creates beautiful, animated word clouds with intelligent layout
 */
class WordCloudRenderer {
  constructor(containerId, options = {}) {
    this.containerId = containerId;
    this.container = document.getElementById(containerId);
    
    if (!this.container) {
      console.error(`Container ${containerId} not found`);
      return;
    }

    // Default options with dashboard theme colors
    this.options = {
      width: options.width || this.container.clientWidth || 800,
      height: options.height || 500,
      colors: options.colors || [
        '#0b92e6', '#3cb95d', '#6f42c1', '#fd7e14', 
        '#dc3545', '#20c997', '#6610f2', '#e83e8c',
        '#17a2b8', '#ffc107'
      ],
      fontFamily: options.fontFamily || 'Inter, system-ui, -apple-system, sans-serif',
      minFontSize: options.minFontSize || 14,
      maxFontSize: options.maxFontSize || 72,
      padding: options.padding || 5,
      rotate: options.rotate || (() => {
        // More controlled rotation: 0, -45, or 45 degrees
        const angles = [0, -45, 45];
        return angles[Math.floor(Math.random() * angles.length)];
      }),
      spiral: options.spiral || 'archimedean',
      animationDuration: options.animationDuration || 800,
      tooltipEnabled: options.tooltipEnabled !== false,
      clickEnabled: options.clickEnabled !== false,
      onWordClick: options.onWordClick || null
    };

    this.data = [];
    this.layout = null;
    this.svg = null;
    this.tooltip = null;
  }

  /**
   * Set word data and render the cloud
   * @param {Array} wordData - Array of [word, count] pairs
   */
  render(wordData) {
    try {
      if (!wordData || wordData.length === 0) {
        this.showEmptyState();
        return;
      }

      this.data = this.processData(wordData);
      this.setupSVG();
      if (this.options.tooltipEnabled) {
        this.setupTooltip();
      }
      this.createLayout();
    } catch (error) {
      console.error('Error rendering word cloud:', error);
      this.showErrorState(error.message);
    }
  }

  /**
   * Process raw word data into format needed for d3-cloud
   */
  processData(wordData) {
    // Find min and max counts for scaling
    const counts = wordData.map(item => item[1]);
    const maxCount = Math.max(...counts);
    const minCount = Math.min(...counts);

    // Create scale for font sizes
    const fontScale = d3.scaleLinear()
      .domain([minCount, maxCount])
      .range([this.options.minFontSize, this.options.maxFontSize]);

    return wordData.map((item, index) => ({
      text: item[0],
      count: item[1],
      size: fontScale(item[1]),
      color: this.options.colors[index % this.options.colors.length]
    }));
  }

  /**
   * Setup SVG container
   */
  setupSVG() {
    // Clear existing content
    this.container.innerHTML = '';

    // Create SVG
    this.svg = d3.select(`#${this.containerId}`)
      .append('svg')
      .attr('width', this.options.width)
      .attr('height', this.options.height)
      .attr('class', 'word-cloud-svg');

    // Create main group for words
    this.svg.append('g')
      .attr('class', 'word-cloud-group')
      .attr('transform', `translate(${this.options.width / 2},${this.options.height / 2})`);
  }

  /**
   * Create d3-cloud layout
   */
  createLayout() {
    this.layout = cloud()
      .size([this.options.width, this.options.height])
      .words(this.data)
      .padding(this.options.padding)
      .rotate(this.options.rotate)
      .spiral(this.options.spiral)
      .font(this.options.fontFamily)
      .fontSize(d => d.size)
      .on('end', words => this.draw(words));

    this.layout.start();
  }

  /**
   * Setup tooltip
   */
  setupTooltip() {
    // Remove existing tooltip if any
    d3.select(`#${this.containerId}-tooltip`).remove();

    this.tooltip = d3.select('body')
      .append('div')
      .attr('id', `${this.containerId}-tooltip`)
      .attr('class', 'word-cloud-tooltip')
      .style('position', 'fixed') // Changed from absolute to fixed
      .style('visibility', 'hidden')
      .style('background', 'rgba(0, 0, 0, 0.9)')
      .style('color', 'white')
      .style('padding', '8px 12px')
      .style('border-radius', '6px')
      .style('font-size', '0.875rem')
      .style('font-weight', '500')
      .style('pointer-events', 'none')
      .style('z-index', '10000')
      .style('box-shadow', '0 4px 12px rgba(0,0,0,0.3)')
      .style('transition', 'opacity 0.2s ease')
      .style('white-space', 'nowrap') // Prevent text wrapping
      .style('transform', 'translate(-50%, -100%)') // Center horizontally and position above cursor
      .style('margin-top', '-10px'); // Additional spacing above cursor
  }

  /**
   * Draw words on SVG with animations
   */
  draw(words) {
    const g = this.svg.select('.word-cloud-group');
    const self = this;

    // Bind data
    const text = g.selectAll('text')
      .data(words);

    // Enter new words
    const textEnter = text.enter()
      .append('text')
      .style('font-size', d => `${d.size}px`)
      .style('font-family', this.options.fontFamily)
      .style('fill', d => d.color)
      .style('cursor', this.options.clickEnabled ? 'pointer' : 'default')
      .attr('text-anchor', 'middle')
      .attr('transform', d => `translate(${d.x},${d.y})rotate(${d.rotate})`)
      .text(d => d.text)
      .style('opacity', 0)
      .style('font-weight', function(d) {
        return d.count > self.getMaxCount() * 0.5 ? '600' : '400';
      });

    // Animate entrance with easeOut (built-in d3 easing)
    textEnter.transition()
      .duration(this.options.animationDuration)
      .ease(d3.easeQuadOut)
      .style('opacity', 0.9)
      .delay((d, i) => i * 30);

    // Add hover effects
    textEnter
      .on('mouseenter', function(event, d) { self.handleMouseEnter(event, d, this); })
      .on('mousemove', function(event, d) { self.handleMouseMove(event, d); })
      .on('mouseleave', function(event, d) { self.handleMouseLeave(event, d, this); });

    // Add click handler
    if (this.options.clickEnabled) {
      textEnter.on('click', function(event, d) { self.handleClick(event, d); });
    }
  }

  /**
   * Mouse enter handler
   */
  handleMouseEnter(event, d, element) {
    // Animate using d3 transition for SVG elements
    d3.select(element)
      .transition()
      .duration(200)
      .style('opacity', 1)
      .style('font-size', `${d.size * 1.1}px`)
      .style('filter', 'drop-shadow(0 2px 8px rgba(0,0,0,0.3))');

    // Show tooltip
    if (this.tooltip) {
      const tooltipText = this.getTooltipText(d);
      this.tooltip
        .html(tooltipText)
        .style('visibility', 'visible')
        .style('opacity', '1');
    }
  }

  /**
   * Mouse move handler
   */
  handleMouseMove(event, d) {
    if (this.tooltip) {
      this.tooltip
        .style('top', `${event.clientY}px`) // Changed from pageY to clientY for fixed positioning
        .style('left', `${event.clientX}px`); // Changed from pageX to clientX for fixed positioning
    }
  }

  /**
   * Mouse leave handler
   */
  handleMouseLeave(event, d, element) {
    // Restore original state
    d3.select(element)
      .transition()
      .duration(200)
      .style('opacity', 0.9)
      .style('font-size', `${d.size}px`)
      .style('filter', 'none');

    // Hide tooltip
    if (this.tooltip) {
      this.tooltip
        .style('visibility', 'hidden')
        .style('opacity', '0');
    }
  }

  /**
   * Click handler
   */
  handleClick(event, d) {
    if (this.options.onWordClick) {
      this.options.onWordClick(d);
    }
  }

  /**
   * Get tooltip text
   */
  getTooltipText(d) {
    // Override this method based on context (researchers or projects)
    return `"${d.text}" - ${d.count} occurrences`;
  }

  /**
   * Get max count from data
   */
  getMaxCount() {
    return Math.max(...this.data.map(d => d.count));
  }

  /**
   * Show empty state
   */
  showEmptyState() {
    this.container.innerHTML = `
      <div class="word-cloud-empty-state">
        <i class="bi bi-tags" style="font-size: 3rem; color: #dee2e6;"></i>
        <p class="mt-3 text-muted">No keywords available</p>
      </div>
    `;
  }

  /**
   * Show error state
   */
  showErrorState(message) {
    this.container.innerHTML = `
      <div class="word-cloud-empty-state">
        <i class="bi bi-exclamation-triangle text-danger" style="font-size: 3rem;"></i>
        <p class="mt-3 text-danger">Error loading word cloud</p>
        <small class="text-muted">${message}</small>
      </div>
    `;
  }

  /**
   * Destroy and cleanup
   */
  destroy() {
    if (this.tooltip) {
      this.tooltip.remove();
    }
    if (this.container) {
      this.container.innerHTML = '';
    }
  }
}

// Export for use in other modules
export default WordCloudRenderer;
